import { Plus } from "lucide-react";
import { useState } from "react";
import type { LinkProps } from "react-router";
import { Link, useMatch } from "react-router";

import { AddCardDialogContent } from "@/components/AddCardDialogContent";
import { AddDeckDialogContent } from "@/components/AddDeckDialogContent";
import { ImportDeckDialogContent } from "@/components/ImportDeckDialogContent";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { SidebarTrigger, useSidebar } from "@/components/ui/sidebar";
import { useIsMobile } from "@/hooks/use-mobile";
import { useActiveRouteFriendName } from "@/hooks/useActiveRouteFriendlyName";
import { useAddSampleDeck } from "@/hooks/useAddSampleDeck";

const navItems = [
  { name: "My Decks", href: "/decks" },
  { name: "My Account", href: "/account" },
];

export const Header = () => {
  const [addDeckDialogOpen, setAddDeckDialogOpen] = useState(false);
  const [addCardDialogOpen, setAddCardDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  const addSampleDeck = useAddSampleDeck();
  const isMobile = useIsMobile();
  const activeRouteFriendlyName = useActiveRouteFriendName();
  const { toggleSidebar } = useSidebar();

  return (
    <>
      <header className="flex items-center justify-between px-6 py-3 shadow-sm z-20">
        <div className="flex items-top gap-1">
          {isMobile ? (
            <>
              <a href="#" onClick={toggleSidebar} className="text-xl font-semibold">
                17reviews
              </a>
              <SidebarTrigger className="pt-1" />
            </>
          ) : (
            <Link to="/decks" className="text-xl font-semibold">
              17reviews
            </Link>
          )}
        </div>
        <div className="flex items-center gap-3">
          <NavigationMenu>
            <NavigationMenuList>
              {isMobile ? (
                <NavigationMenuItem>
                  <NavigationMenuTrigger>{activeRouteFriendlyName}</NavigationMenuTrigger>
                  <NavigationMenuContent>
                    {navItems.map((item) => (
                      <ReactRouterNavigationMenuLink
                        to={item.href}
                        key={item.href}
                        displayName={item.name}
                      />
                    ))}
                  </NavigationMenuContent>
                </NavigationMenuItem>
              ) : (
                navItems.map((item) => (
                  <ReactRouterNavigationMenuLink
                    to={item.href}
                    key={item.href}
                    displayName={item.name}
                    showIndicator={true}
                  />
                ))
              )}
            </NavigationMenuList>
          </NavigationMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon-md">
                <Plus />
                <span className="sr-only">Add new</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setAddCardDialogOpen(true)} className="w-full">
                New card
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setAddDeckDialogOpen(true)} className="w-full">
                New deck
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setImportDialogOpen(true)} className="w-full">
                Import deck
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={addSampleDeck} className="w-full">
                New sample deck
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/generate" className="w-full">
                  Generate deck with AI
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      <Dialog open={addDeckDialogOpen} onOpenChange={setAddDeckDialogOpen}>
        <AddDeckDialogContent />
      </Dialog>
      <Dialog open={addCardDialogOpen} onOpenChange={setAddCardDialogOpen}>
        <AddCardDialogContent closeDialog={() => setAddCardDialogOpen(false)} />
      </Dialog>
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <ImportDeckDialogContent closeDialog={() => setImportDialogOpen(false)} />
      </Dialog>
    </>
  );
};

interface ReactRouterNavigationMenuLinkProps extends Omit<LinkProps, "children"> {
  to: string;
  displayName?: string;
  showIndicator?: boolean;
}

const ReactRouterNavigationMenuLink = ({
  to,
  displayName,
  showIndicator,
  ...props
}: ReactRouterNavigationMenuLinkProps) => {
  // We want the link to be active if the current path starts with the link's path.
  const active = !!useMatch(`${to}/*`);
  return (
    <NavigationMenuLink
      asChild
      data-active={active}
      className={
        "relative whitespace-nowrap" + (showIndicator ? " data-[active=true]:bg-background" : "")
      }
    >
      <Link to={to} {...props}>
        {displayName}
        {active && showIndicator && (
          <div className="absolute bottom-0 left-0 w-full h-0.5 z-10 bg-secondary" />
        )}
      </Link>
    </NavigationMenuLink>
  );
};
